import React from "react";
import { useNavigate } from "react-router-dom";
import {
  User, Bell, Heart, Wallet, Bike, PlusCircle, List, Store, CreditCard,
  Rocket, Ribbon, Settings, LogOut, ChevronLeft, Gauge, ShieldCheck, FileCheck,
  Megaphone, Users, Eye, Share2, Layers, Navigation,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { getDisplayName, getDisplayInitial } from "../lib/displayName";
import { useAppVersion } from "../lib/useAppVersion";
import { Verified } from "../components/Verified";

export default function Profile() {
  const { user, guest, logout } = useAuth();
  const navigate = useNavigate();
  const version = useAppVersion();

  if (guest) {
    return (
      <div className="animate-fade-in-up max-w-lg mx-auto">
        <div className="bg-white rounded-3xl border border-[#E8EAF2] p-8 text-center">
          <div className="w-20 h-20 rounded-3xl bg-[#7C5CFF] mx-auto flex items-center justify-center text-white"><User size={40} /></div>
          <h1 className="text-xl font-black text-[#161629] mt-4">ضيف</h1>
          <p className="text-[#6B7280]">أنت تتصفح كزائر</p>
          <button data-testid="guest-go-login" onClick={async () => { await logout(); navigate("/login"); }} className="w-full flex items-center justify-center gap-2 mt-6 py-4 rounded-2xl bg-[#FF3D5A] text-white font-black"><LogOut size={20} /> تسجيل دخول / إنشاء حساب</button>
        </div>
      </div>
    );
  }
  if (!user) return null;

  const isAdmin = !!user.is_admin;
  const subUntil = user.subscription_active_until ? new Date(user.subscription_active_until) : null;

  return (
    <div className="animate-fade-in-up max-w-2xl mx-auto">
      {/* Profile card */}
      <div className="bg-white rounded-3xl border border-[#E8EAF2] overflow-hidden">
        <div className="h-28 bg-gradient-to-l from-[#FF3D5A] to-[#FF6B9A]" style={user.cover_base64 ? { backgroundImage: `url(${user.cover_base64})`, backgroundSize: "cover" } : {}} />
        <div className="px-6 pb-6 -mt-12">
          {user.picture_base64 || user.picture ? (
            <img src={user.picture_base64 || user.picture} alt="" className="w-24 h-24 rounded-3xl object-cover border-4 border-white" />
          ) : (
            <div className="w-24 h-24 rounded-3xl bg-[#7C5CFF] text-white flex items-center justify-center text-4xl font-black border-4 border-white">{getDisplayInitial(user)}</div>
          )}
          <div className="flex items-center gap-2 mt-3">
            <h1 className="text-xl font-black text-[#161629]">{isAdmin ? "نطاق للتسوق" : getDisplayName(user)}</h1>
            {user.verified && <Verified size={18} />}
          </div>
          <p className="text-sm text-[#6B7280]">{user.email}</p>
          <span className="inline-block mt-2 px-3 py-1 rounded-lg bg-[#F0F2FA] text-xs font-black text-[#161629]">
            {isAdmin ? "نطاق للتسوق ✓" : user.role === "seller" ? (user.specialization_name || (user.seller_type === "online" ? "متجر إلكتروني" : "متجر محلي")) : user.role === "driver" ? "سائق" : "مشتري"}
          </span>
          {user.role === "seller" && subUntil && (
            <div className={`flex items-center gap-2 mt-3 px-3 py-2 rounded-xl text-xs font-bold ${user.subscription_active ? "bg-[#D9F8E8] text-[#00D26A]" : "bg-[#FFE5EA] text-[#FF3D5A]"}`}>
              <ShieldCheck size={16} /> {user.subscription_active ? `الاشتراك حتى ${subUntil.toLocaleDateString("ar")}` : "الاشتراك منتهي"}
            </div>
          )}
        </div>
      </div>

      {user.role === "driver" && (
        <Section title="حساب السائق">
          <Item testId="go-driver-verify" icon={FileCheck} text={user.driver_verification_status === "approved" ? "توثيق الحساب (موثّق ✅)" : "توثيق حساب السائق"} to="/driver-verify" />
          <Item testId="go-driver-dashboard" icon={Navigation} text="لوحة السائق" to="/driver-dashboard" />
        </Section>
      )}

      {isAdmin && (
        <Section title="لوحة المالك">
          <Item testId="go-admin-dashboard" icon={Gauge} text="لوحة الإحصائيات" to="/admin" />
          <Item testId="go-admin-broadcast" icon={Megaphone} text="إرسال إشعار للمستخدمين" to="/admin/broadcast" />
          <Item testId="go-admin-categories" icon={List} text="إدارة القوائم (متشجّرة)" to="/admin/categories" />
          <Item testId="go-admin-specializations" icon={Store} text="تخصصات المتاجر" to="/admin/specializations" />
          <Item testId="go-admin-disputes" icon={ShieldCheck} text="فض النزاعات" to="/admin/disputes" />
          <Item testId="go-admin-withdrawals" icon={CreditCard} text="سحوبات التجار والسائقين" to="/admin/withdrawals" />
          <Item testId="go-admin-interests" icon={Layers} text="إدارة الاهتمامات" to="/admin/interests" />
          <Item testId="go-admin-platform-social" icon={Share2} text="روابط التواصل العامة" to="/admin/platform-social" />
          <Item testId="go-admin-payments" icon={CreditCard} text="إدارة طرق الدفع" to="/admin/payment-methods" />
          <Item testId="go-admin-subscription" icon={Ribbon} text="اشتراك البائعين" to="/admin/subscription" />
          <Item testId="go-admin-driver-sub" icon={Bike} text="اشتراك السائقين" to="/admin/driver-subscription" />
          <Item testId="go-admin-promotions" icon={Rocket} text="باقات تمويل المنتجات" to="/admin/promo-packages" />
          <Item testId="go-admin-driver-apps" icon={FileCheck} text="طلبات توثيق السائقين" to="/admin/driver-applications" />
          <Item testId="go-admin-cities" icon={List} text="إدارة المدن" to="/admin/cities" />
          <Item testId="go-admin-delivery" icon={Bike} text="إعدادات التوصيل" to="/admin/delivery-settings" />
          <Item testId="go-admin-chats" icon={Eye} text="مراقبة المحادثات" to="/admin/chats" />
          <Item testId="go-admin-followers" icon={Users} text="إدارة المتابعين" to="/admin/followers" />
          <Item testId="go-admin-users" icon={Users} text="إدارة المستخدمين" to="/admin/users" />
        </Section>
      )}

      <Section title="الحساب">
        <Item testId="go-edit-profile" icon={User} text="تعديل الملف الشخصي" to="/edit-profile" />
        <Item testId="go-notifications" icon={Bell} text="الإشعارات" to="/notifications" />
        <Item testId="go-favorites" icon={Heart} text="تفضيلاتي" to="/my-favorites" />
        <Item testId="go-wallet" icon={Wallet} text={`المحفظة • ${(user.wallet_balance || 0).toFixed(2)} د.ل`} to="/wallet" />
        {(user.role === "buyer" || user.role === "seller") && <Item testId="go-drivers" icon={Bike} text="السائقون القريبون" to="/drivers" />}
        {user.role === "seller" && (
          <>
            <Item testId="go-add-product" icon={PlusCircle} text="إضافة منتج جديد" to="/add-product" />
            <Item testId="go-store-categories" icon={List} text="قوائم متجري الخاصة" to="/store/categories" />
            <Item testId="go-my-store" icon={Store} text="صفحتي العامة" to={`/seller/${user.user_id}`} />
            <Item testId="go-my-payments" icon={CreditCard} text="طرق الدفع الخاصة بمتجري" to="/seller/payment-methods" />
            <Item testId="go-my-promotions" icon={Rocket} text="إعلاناتي المموَّلة" to="/my-promotions" />
            <Item testId="go-subscription" icon={Ribbon} text="الاشتراك الشهري" to="/subscription" />
          </>
        )}
        <Item testId="go-settings" icon={Settings} text="الإعدادات" to="/settings" />
        <button data-testid="logout-button" onClick={async () => { if (window.confirm("تسجيل الخروج؟")) { await logout(); navigate("/login"); } }} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white border border-[#E8EAF2] mb-2 text-[#FF3D5A]">
          <LogOut size={22} /> <span className="flex-1 text-start font-bold">تسجيل الخروج</span>
        </button>
      </Section>
      <p className="text-center text-[#9CA3AF] text-xs mt-6">نطاق للتسوق • الإصدار {version}</p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-6">
      <p className="text-sm font-black text-[#6B7280] mb-3">{title}</p>
      {children}
    </div>
  );
}
function Item({ icon: Icon, text, to, testId }) {
  const navigate = useNavigate();
  return (
    <button data-testid={testId} onClick={() => navigate(to)} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-white border border-[#E8EAF2] mb-2 hover:brand-shadow-sm transition-shadow">
      <Icon size={22} className="text-[#161629]" />
      <span className="flex-1 text-start font-bold text-[#161629]">{text}</span>
      <ChevronLeft size={18} className="text-[#9CA3AF]" />
    </button>
  );
}
